/**
 * Units - Extended types for cross-hangar unit management
 *
 * These types extend the base hangar-units types to support
 * the standalone Units page with building/location context.
 *
 * Based on architecture docs:
 * - docs/005-data-schema/planemax-consolidated-architecture.md
 * - docs/007-ui/planemax-unit-types-architecture.md
 */

import type { Database } from '@planmax/shared-types'

// Re-export base types for convenience
export {
  type HangarUnitStatus,
  type HangarUnitRow,
  type HangarUnitInsert,
  type HangarUnitUpdate,
  type HangarUnitFormData,
  HANGAR_UNIT_STATUS_LABELS,
  HANGAR_UNIT_STATUSES,
} from './hangar-units'

import type { HangarUnitStatus } from './hangar-units'

// ============================================
// ENUMS - Match database enums
// ============================================

/** Unit structural type - describes the physical form of the unit */
export type UnitType =
  | 'conventional_hangar'   // Large open space, multiple aircraft, no internal walls
  | 'box_hangar'            // Individual enclosed unit with own door
  | 't_hangar'              // T-shaped individual unit
  | 'shade'                 // Shade port / canopy structure (parks aircraft)
  | 'tiedown'               // Individual tiedown spot
  | 'ramp'                  // Individual ramp parking position
  | 'office'                // Office/admin space
  | 'storage'               // Non-aircraft storage room
  | 'shop'                  // Maintenance shop / workshop bay
  | 'custom'                // User-defined unit type

/** All unit types */
export const UNIT_TYPES: UnitType[] = [
  // Hangar types
  'conventional_hangar',
  'box_hangar',
  't_hangar',
  'shade',
  // Outdoor types
  'tiedown',
  'ramp',
  // Commercial / other types
  'office',
  'storage',
  'shop',
  'custom',
]

/** Unit type display labels */
export const UNIT_TYPE_LABELS: Record<UnitType, string> = {
  conventional_hangar: 'Conventional Hangar',
  box_hangar: 'Box Hangar',
  t_hangar: 'T-Hangar',
  shade: 'Shade Port',
  tiedown: 'Tiedown Spot',
  ramp: 'Ramp Position',
  office: 'Office Space',
  storage: 'Storage',
  shop: 'Shop / Workshop',
  custom: 'Custom',
}

/** Unit designation - soft business label for usage */
export type UnitDesignation =
  | 'storage'
  | 'maintenance'
  | 'transient'
  | 'corporate'
  | 'private_lease'
  | 'shared'
  | 'custom'

/** All unit designations */
export const UNIT_DESIGNATIONS: UnitDesignation[] = [
  'storage',
  'maintenance',
  'transient',
  'corporate',
  'private_lease',
  'shared',
  'custom',
]

/** Unit designation display labels */
export const UNIT_DESIGNATION_LABELS: Record<UnitDesignation, string> = {
  storage: 'Storage',
  maintenance: 'Maintenance / MRO',
  transient: 'Transient / Overnight',
  corporate: 'Corporate',
  private_lease: 'Private Lease',
  shared: 'Shared / Community',
  custom: 'Custom',
}

/** Capacity strategy - determines how aircraft are placed */
export type CapacityStrategy = 'single' | 'stackable' | 'area_based'

/** All capacity strategies */
export const CAPACITY_STRATEGIES: CapacityStrategy[] = ['single', 'stackable', 'area_based']

/** Capacity strategy display labels */
export const CAPACITY_STRATEGY_LABELS: Record<CapacityStrategy, string> = {
  single: 'Single Aircraft',
  stackable: 'Multiple (Positioned)',
  area_based: 'Area-Based',
}

/**
 * Door type enum — mirrors the DB `door_type` enum.
 * Previously hand-written and missing 'rolling', which the DB and the building
 * templates both emit.
 */
export type DoorType = Database['public']['Enums']['door_type']

/** All door types */
export const DOOR_TYPES: DoorType[] = ['bifold', 'sliding', 'hydraulic', 'manual', 'rolling']

/** Door type display labels */
export const DOOR_TYPE_LABELS: Record<DoorType, string> = {
  bifold: 'Bi-Fold',
  sliding: 'Sliding',
  hydraulic: 'Hydraulic',
  manual: 'Manual',
  rolling: 'Rolling',
}

/** Rate type enum */
export type RateType = 'flat' | 'per_sqft' | 'per_night'

/** All rate types */
export const RATE_TYPES: RateType[] = ['flat', 'per_sqft', 'per_night']

/** Rate type display labels */
export const RATE_TYPE_LABELS: Record<RateType, string> = {
  flat: 'Flat Rate',
  per_sqft: 'Per Sq Ft',
  per_night: 'Per Night',
}

// ============================================
// STATUS (from base types)
// ============================================

/** Unit status - alias for consistency */
export type UnitStatus = HangarUnitStatus

/** All unit statuses */
export const UNIT_STATUSES: UnitStatus[] = ['available', 'leased', 'maintenance']

/** Unit status display labels */
export const UNIT_STATUS_LABELS: Record<UnitStatus, string> = {
  available: 'Available',
  leased: 'Leased',
  maintenance: 'Maintenance',
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Check if unit type needs aircraft constraints (door specs, wingspan limits, etc.)
 * Office type does NOT need aircraft constraints
 */
export const isAircraftUnit = (type: UnitType): boolean =>
  ['conventional_hangar', 'box_hangar', 't_hangar', 'shade', 'tiedown', 'ramp'].includes(type)

/**
 * Check if unit type is an enclosed hangar (with interior dimensions and doors)
 */
export const isEnclosedHangarUnit = (type: UnitType): boolean =>
  ['conventional_hangar', 'box_hangar', 't_hangar'].includes(type)

/** Backward-compatible alias */
export const isHangarType = isEnclosedHangarUnit

/**
 * Check if unit type is outdoor (tiedown or ramp - no doors required)
 */
export const isOutdoorUnitType = (type: UnitType): boolean =>
  ['tiedown', 'ramp'].includes(type)

/**
 * Get default capacity strategy based on unit structural type
 */
export const getDefaultCapacityStrategy = (type: UnitType): CapacityStrategy => {
  const defaults: Record<UnitType, CapacityStrategy> = {
    conventional_hangar: 'stackable',
    box_hangar: 'single',
    t_hangar: 'single',
    shade: 'stackable',
    tiedown: 'single',
    ramp: 'single',
    office: 'single',
    storage: 'single',
    shop: 'single',
    custom: 'single',
  }
  return defaults[type]
}

/**
 * Group unit types for filter UI
 */
export const UNIT_TYPE_GROUPS = {
  hangars: ['conventional_hangar', 'box_hangar', 't_hangar', 'shade'] as UnitType[],
  outdoor: ['tiedown', 'ramp'] as UnitType[],
  commercial: ['office', 'storage', 'shop', 'custom'] as UnitType[],
}

// ============================================
// TIEDOWN TYPE CONFIG
// ============================================

/**
 * Tiedown-specific configuration stored in type_config JSON column
 * Used for outdoor parking spots
 */
export interface TiedownTypeConfig {
  /** Surface material (concrete, asphalt, gravel, grass) */
  surface_type?: 'concrete' | 'asphalt' | 'gravel' | 'grass'
  /** Number of tie-down anchor points */
  tie_down_points?: number
  /** Whether spot is covered (has canopy/shade) */
  covered?: boolean
  /** Canopy height if covered */
  canopy_height_ft?: number | null
  /** Has wheel chocks available */
  has_chocks?: boolean
  /** Lighting type (none, standard, led) */
  lighting?: 'none' | 'standard' | 'led'
  /** Power hookup capacity (none, 15amp, 30amp, 50amp) */
  power_hookup?: 'none' | '15amp' | '30amp' | '50amp'
}

/** Default tiedown config for new tiedown units */
export const DEFAULT_TIEDOWN_CONFIG: TiedownTypeConfig = {
  surface_type: 'concrete',
  tie_down_points: 3,
  covered: false,
  canopy_height_ft: null,
  has_chocks: true,
  lighting: 'standard',
  power_hookup: 'none',
}

/** Surface type options for tiedown config */
export const SURFACE_TYPES = ['concrete', 'asphalt', 'gravel', 'grass'] as const
export const SURFACE_TYPE_LABELS: Record<typeof SURFACE_TYPES[number], string> = {
  concrete: 'Concrete',
  asphalt: 'Asphalt',
  gravel: 'Gravel',
  grass: 'Grass',
}

/** Lighting options for tiedown config */
export const LIGHTING_OPTIONS = ['none', 'standard', 'led'] as const
export const LIGHTING_LABELS: Record<typeof LIGHTING_OPTIONS[number], string> = {
  none: 'None',
  standard: 'Standard',
  led: 'LED',
}

/** Power hookup options for tiedown config */
export const POWER_OPTIONS = ['none', '15amp', '30amp', '50amp'] as const
export const POWER_LABELS: Record<typeof POWER_OPTIONS[number], string> = {
  none: 'None',
  '15amp': '15 Amp',
  '30amp': '30 Amp',
  '50amp': '50 Amp',
}

// ============================================
// INTERFACES
// ============================================

/**
 * Extended unit type for table display with building/location context.
 * Used in the standalone Units page to show units across all buildings.
 * Matches database `units` table with joined building/location data.
 */
export interface UnitTableRow {
  id: string
  unit_code: string
  name: string | null
  sequence: number
  area_id: string
  organization_id: string
  location_id: string

  // Classification
  type: UnitType | null
  designation: UnitDesignation | null
  capacity_strategy: CapacityStrategy | null

  // Interior Dimensions (for hangars)
  interior_width: number | null
  interior_depth: number | null
  interior_height: number | null
  usable_sqft: number | null

  // T-hangar specific dimensions (legacy support)
  door_width: number | null
  depth: number | null
  tail_width: number | null

  // Door Specifications
  door_height: number | null
  door_type: DoorType | null

  // Aircraft Constraints
  max_wingspan: number | null
  max_length: number | null
  max_tail_height: number | null

  // Position within Building
  offset_x: number | null
  offset_y: number | null
  unit_rotation: number | null

  // Status
  status: UnitStatus
  is_leasable: boolean | null
  current_lease_id: string | null

  // Features
  climate_controlled: boolean | null
  heated: boolean | null
  electrical_service: string | null
  features: Record<string, unknown> | null

  // Pricing
  default_monthly_rate: number | null
  default_transient_rate: number | null
  rate_type: RateType | null
  pricing_override: boolean | null
  unit_monthly_rate: number | null
  unit_rate_per_sqft: number | null

  // Joined area info
  area_name: string
  area_code: string
  area_type: string

  // Joined location info (via building)
  location_name: string | null
  location_code: string | null

  // Audit
  created_at: string
  updated_at: string | null
}

/**
 * Filters for querying units across buildings
 */
export interface UnitFilters {
  /** Filter by unit type */
  type?: UnitType[]
  /** Filter by designation */
  designation?: UnitDesignation[]
  /** Filter by status */
  status?: UnitStatus[]
  /** Filter by capacity strategy */
  capacityStrategy?: CapacityStrategy[]
  /** Filter by area (building) ID */
  areaId?: string
  /** Filter by location ID */
  locationId?: string
  /** Filter by leasable status */
  isLeasable?: boolean
  /** Search term (matches unit_code, name, building_name) */
  search?: string
}

/**
 * Unit form data for create/edit operations
 * Includes all fields that can be set via the UI form
 */
export interface UnitFormData {
  // Basic Info
  unit_code: string
  name: string | null
  area_id: string
  type: UnitType
  designation: UnitDesignation | null
  capacity_strategy: CapacityStrategy
  status: UnitStatus
  is_leasable: boolean
  sequence: number

  // Interior Dimensions (for hangars)
  interior_width: number | null
  interior_depth: number | null
  interior_height: number | null
  usable_sqft: number | null

  // T-hangar specific (legacy)
  depth: number | null
  tail_width: number | null

  // Door Specifications
  door_width: number | null
  door_height: number | null
  door_type: DoorType | null

  // Aircraft Constraints
  max_wingspan: number | null
  max_length: number | null
  max_tail_height: number | null

  // Position
  offset_x: number
  offset_y: number
  unit_rotation: number | null

  // Features
  climate_controlled: boolean
  heated: boolean
  electrical_service: string | null

  // Pricing
  default_monthly_rate: number | null
  default_transient_rate: number | null
  rate_type: RateType | null
  pricing_override: boolean
  unit_monthly_rate: number | null
  unit_rate_per_sqft: number | null
}

/**
 * Building option for dropdown selectors
 */
export interface BuildingOption {
  id: string
  name: string
  building_code: string
  type: string
  location_name: string | null
}
